// shared/plLedger.ts — 経営PL「元帳で深掘り」 の型と PL 科目の束ね方 (server / client 共用)
//
// 元帳 (経理の仕訳) を PL の科目に対応づけた fact_pl_ledger を、 PL の小計単位で見せる。
// 対応の根拠と突合結果 = aggregation/pl/script/pl_ledger.R の冒頭 / worklog 20260926。
//   ・元帳に無い行 (本部人件費 / 本部経費 = 本部配賦・支払利息) は plOnly に置き、
//     「PL にはあるが元帳には無い」 として別行で見せる。
//   ・器機賃借料 は PL では (賃借料)/(リース料) の 2 行だが元帳は 1 科目なので合算で対応する。
//   ・医業外損益 は 雑収入 + 受取利息 − 雑損失 − 支払利息。 sign で向きを持つ。

export type PlLedgerGroup = {
  key: string;          // PL の小計名 (fact_pl_monthly の metric)
  label: string;
  metrics: string[];    // 元帳と対応する PL 科目
  plOnly: string[];     // PL にだけある行 (元帳に仕訳が無い)
  revenue?: boolean;    // true = 増えると良い (医業外損益)
  sign?: Record<string, number>;
  /** グループの一部を 1 つの科目のように選ぶ (「経費計」 = 経費等 − 研究費 − 研修費 − 本部経費) */
  subsets?: { key: string; label: string; metrics: string[] }[];
};

/** 科目セレクタの値: PL 科目名 or subsets の key (先頭 "@") */
export function metricsOfSelection(g: PlLedgerGroup, sel: string | null): string[] {
  if (!sel) return g.metrics;
  const sub = g.subsets?.find((x) => x.key === sel);
  return sub ? sub.metrics : [sel];
}

export const PL_LEDGER_GROUPS: PlLedgerGroup[] = [
  { key: "材料費計", label: "材料費",
    metrics: ["医薬品費", "診療材料費", "医療消耗器具備品費", "給食用材料費"], plOnly: [] },
  { key: "人件費計", label: "人件費",
    metrics: ["給料", "賞与", "退職給付費用", "法定福利費"], plOnly: ["本部人件費"] },
  { key: "委託費計", label: "委託費",
    metrics: ["検査委託費", "給食委託費", "寝具委託費", "医事委託費", "清掃委託費", "保守委託費", "その他の委託費"],
    plOnly: [] },
  { key: "設備関係費計", label: "設備関係費",
    // 器機設備保険料 は元帳に対応コードが無い (2026-09 時点で PL も 0) → 元帳外として並べる
    metrics: ["減価償却費", "器機賃借料", "地代家賃", "修繕費", "固定資産税等", "器機保守料", "車両関係費"],
    plOnly: ["器機設備保険料"] },
  { key: "経費等", label: "経費等",
    metrics: ["研究費", "研修費", "福利厚生費", "旅費交通費", "職員被服費", "通信費", "広告宣伝費", "消耗品費",
              "消耗器具備品費", "会議費", "水道光熱費", "保険料", "交際費", "募集費", "寄付金", "諸会費",
              "租税公課", "雑費"],
    plOnly: ["本部経費"],
    subsets: [{ key: "@経費計", label: "経費計 (研究費・研修費を除く)",
                metrics: ["福利厚生費", "旅費交通費", "職員被服費", "通信費", "広告宣伝費", "消耗品費", "消耗器具備品費",
                          "会議費", "水道光熱費", "保険料", "交際費", "募集費", "寄付金", "諸会費", "租税公課", "雑費"] }] },
  { key: "医業外損益", label: "医業外損益",
    metrics: ["雑収入", "受取利息", "雑損失"], plOnly: ["支払利息"], revenue: true,
    sign: { 雑損失: -1, 支払利息: -1 } },
];

export const PL_LEDGER_METRICS: string[] = PL_LEDGER_GROUPS.flatMap((g) => g.metrics);

/** PL の科目名 (差異ランキング等の表示名) → ドリルで開くグループと科目 */
export function findLedgerTarget(metric: string): { group: string; metric: string | null } | null {
  const alias: Record<string, string> = {
    "器機賃借料（リース料）": "器機賃借料", "器機賃借料（賃借料）": "器機賃借料",
    "事務局人件費": "本部人件費", "事務局経費": "本部経費", "小計": "人件費計",
  };
  // 経費計 は 経費等 と別の数字 (研究費・研修費・本部経費を含まない)。 部分集合として開く
  if (metric === "経費計") return { group: "経費等", metric: "@経費計" };
  const m = alias[metric] ?? metric;
  for (const g of PL_LEDGER_GROUPS) {
    if (g.key === m) return { group: g.key, metric: null };
    if (g.metrics.includes(m)) return { group: g.key, metric: m };
    if (g.plOnly.includes(m)) return { group: g.key, metric: null };
  }
  return null;
}

/** 科目 × 月 の PL 値と元帳合計 (千円)。 元帳の無い月・科目は ledger = null */
export type PlLedgerReconRow = { date: string; metric: string; pl: number | null; ledger: number | null };

export type PlLedgerSource = { file: string; fy: number; first_month: string; last_month: string; rows: number; built_at: string };

export type PlLedgerSummaryResponse = {
  available: boolean;
  months: string[];          // 元帳がある月 (YYYY-MM-01)
  recon: PlLedgerReconRow[];
  sources: PlLedgerSource[];
  sqlMs: number;
};

/** 月 × 科目 × 元帳科目 × 取引先 × 項目 の集計 (円) */
export type PlLedgerItemRow = {
  date: string; metric: string; code: string; acct_name: string;
  vendor: string; item: string; hidden: boolean;
  amount: number; headcount: number | null; n: number;
};
export type PlLedgerItemsResponse = { rows: PlLedgerItemRow[]; sqlMs: number };

export type PlLedgerLine = {
  date: string | null; ym: string; voucher_no: number | null; partner: string; n_lines: number;
  vendor: string; item: string; tekiyo: string; hidden: boolean; amount: number;
};
export type PlLedgerLinesResponse = { rows: PlLedgerLine[]; truncated: boolean; sqlMs: number };
